import { FC, useCallback, useMemo, useState } from 'react';
import { BrokerDto, DispatchReviewDto, DriverDto, EquipmentDto, FreightBillingLineItemDto, OrderByType, OrderDto, OrderReviewDto, OrderReviewLineItemDto } from '../../../dtos';
import _, { } from 'lodash';
import { AssignmentType } from '../../../dtos/generated/AssignmentType';
import { Box, FormControl, FormHelperText, FormLabel, Grid, IconButton, MenuItem, Select, SelectChangeEvent, Tab, Tooltip, Typography } from '@mui/material';
import { IEntityAutocomplete } from '../../CommonInputs/IEntityAutocomplete';
import { FormInput } from '../../CoreLib/library';
import { AddCircle, Error } from '@mui/icons-material';
import { useGetRegionQuery } from '../../../store/generated/generatedApi';
import { HaulToFromForm, MemoForm, SelectableList, TimePicker, EquipmentSelect } from '../../CommonInputs';
import { FreightBillingLineItemTable } from './FreightBillingLineItemTable';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { isErrorInMap } from './useOrderReviewForm';
import { TicketFormGeneralTab } from './TicketFormGeneralTab';
import { getUniqueKey } from '../../../util/getUniqueKey';
import { adjustSecondDateToMatchFirst } from '../../../util/adjustDates';
import { SelectOrderReviewLineItemStepperDialog } from './TicketEntry/SelectOrderReviewLineItemStepperDialog';
import { LineItemTypes } from '../../../util';

export interface IDispatchReviewTableProps {
    brokers: BrokerDto[];
    disabled: boolean;
    dispatchReviews: DispatchReviewDto[];
    drivers: DriverDto[];
    setDispatches: (dispatchReviews: React.SetStateAction<DispatchReviewDto[]>) => void;
    addFreightBillingLineItem: (dispatchIndex: number) => void;
    removeFreightBillingLineItem: (dispatchIndex: number, index: number) => void;
    dispatchErrors: Map<number, Map<keyof DispatchReviewDto, string>>;
    freightErrors: Map<number, Map<number, Map<keyof FreightBillingLineItemDto, string>>>;
    getOrderById: (orderId: string) => OrderDto | undefined;
    date: Date;
    addDispatchReviewForOrderReviewLineItem: (orderReview: OrderReviewDto, orderReviewLineItem: OrderReviewLineItemDto) => void;
    handleUpdatedFreightBillingNumber: (updatedFreightBillingNumber: string, dispatchIndex: number, freightBillingLineItemIndex: number) => void;
    disableAdd: boolean;
}

export const DispatchTicketEntryTable: FC<IDispatchReviewTableProps> = (props) => {
    const {
        brokers,
        disabled,
        dispatchReviews,
        drivers,
        setDispatches,
        removeFreightBillingLineItem,
        addFreightBillingLineItem,
        dispatchErrors,
        freightErrors,
        getOrderById,
        date,
        addDispatchReviewForOrderReviewLineItem,
        disableAdd,
        handleUpdatedFreightBillingNumber
    } = props;

    const { data: regions } = useGetRegionQuery({ searchText: '', sorting: ['NAME+'], page: 0, pageSize: 100000, includeInactive: false });

    const [selectedTab, setSelectedTab] = useState('0');
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(0);
    const [selectOrderReviewLineItemDialogOpen, setSelectOrderReviewLineItemDialogOpen] = useState(false);

    const handleRemoveFreightBillingLineItem = useCallback((dispatchIndex: number) => (freightLineItemIndex: number) => removeFreightBillingLineItem(dispatchIndex, freightLineItemIndex), [removeFreightBillingLineItem]);
    const updateDispatch = useCallback(
        (index: number, fieldName: string, value: any) => {
            var updatedDispatchReviews = _.cloneDeep(dispatchReviews);
            var updatedDispatchReview = { ...updatedDispatchReviews[index] };
            updatedDispatchReview[fieldName] = value;

            if (fieldName === 'assignmentType') {
                if (updatedDispatchReview.assignmentType === AssignmentType.Broker) {
                    updatedDispatchReview.driverId = undefined;
                    updatedDispatchReview.driver = undefined;
                    updatedDispatchReview.equipmentId = undefined;
                    updatedDispatchReview.equipment = undefined;
                } else {
                    updatedDispatchReview.brokerId = undefined;
                    updatedDispatchReview.broker = undefined;
                    updatedDispatchReview.equipmentId = undefined;
                    updatedDispatchReview.equipment = undefined;
                }
            }

            updatedDispatchReviews.splice(index, 1, updatedDispatchReview);

            setDispatches(updatedDispatchReviews);
        },
        [dispatchReviews, setDispatches]
    );

    const updateOrderReviewLineItemMemos = useCallback(
        (index: number, fieldName: string, value: any) => {
            var updatedDispatchReviews = _.cloneDeep(dispatchReviews);
            var updatedDispatchReview = { ...updatedDispatchReviews[index] };

            updatedDispatchReviews.forEach(dispatch => {
                if (dispatch.orderLineItem && dispatch.orderLineItem.id === updatedDispatchReview.orderLineItem?.id) {
                    dispatch.orderLineItem[fieldName] = value;
                }
            });

            setDispatches(updatedDispatchReviews);
        },
        [dispatchReviews, setDispatches]
    );

    const handleBrokerChange = useCallback(
        (index: number, broker?: BrokerDto) => {
            var updatedDispatchReviews = _.cloneDeep(dispatchReviews);
            var updatedDispatchReview = { ...updatedDispatchReviews[index] };

            updatedDispatchReview['broker'] = broker;
            updatedDispatchReview['brokerId'] = broker?.id;
            updatedDispatchReviews.splice(index, 1, updatedDispatchReview);

            setDispatches(updatedDispatchReviews);
        },
        [dispatchReviews, setDispatches]
    );

    const handleDriverChange = useCallback(
        (index: number, driver?: DriverDto) => {
            var updatedDispatchReviews = _.cloneDeep(dispatchReviews);
            var updatedDispatchReview = { ...updatedDispatchReviews[index] };

            updatedDispatchReview['driver'] = driver;
            updatedDispatchReview['driverId'] = driver?.id;
            updatedDispatchReview['equipmentId'] = driver?.equipmentId;
            updatedDispatchReview['equipment'] = driver?.equipment;
            updatedDispatchReviews.splice(index, 1, updatedDispatchReview);

            setDispatches(updatedDispatchReviews);
        },
        [dispatchReviews, setDispatches]
    );

    const handleEquipmentChange = useCallback(
        (index: number, equipment?: EquipmentDto) => {
            var updatedDispatchReviews = _.cloneDeep(dispatchReviews);
            var updatedDispatchReview = { ...updatedDispatchReviews[index] };

            updatedDispatchReview['equipment'] = equipment;
            updatedDispatchReview['equipmentId'] = equipment?.id;
            updatedDispatchReviews.splice(index, 1, updatedDispatchReview);

            setDispatches(updatedDispatchReviews);
        },
        [dispatchReviews, setDispatches]
    );

    const checkDispatchForGeneralErrors = useCallback(
        (index: number) => {
            const errorMap = dispatchErrors.get(index);
            const generalFields: (keyof DispatchReviewDto)[] = ['brokerId', 'driverId', 'equipmentId', 'onsiteTime', 'startDate', 'endDate', 'driverComment'];
            return generalFields.some((x) => !!errorMap?.get(x));
        },
        [dispatchErrors]
    );

    const checkDispatchForHaulToFromErrors = useCallback(
        (index: number) => {
            const errorMap = dispatchErrors.get(index);
            const haulToFromFields: (keyof DispatchReviewDto)[] = [
                'haulToAddressLine1',
                'haulToAddressLine2',
                'haulToCity',
                'haulToState',
                'haulToZipCode',
                'haulToCountry',
                'haulToDirections',
                'haulFromAddressLine1',
                'haulFromAddressLine2',
                'haulFromCity',
                'haulFromState',
                'haulFromZipCode',
                'haulFromCountry',
                'haulFromDirections',
            ];
            return haulToFromFields.some((x) => !!errorMap?.get(x));
        },
        [dispatchErrors]
    );

    const checkDispatchForBillingErrors = useCallback(
        (index: number) => {
            const errorMaps = freightErrors.get(index);
            return Array.from(errorMaps?.values() ?? []).some((billingErrorMap) => isErrorInMap(billingErrorMap));
        },
        [freightErrors]
    );

    const checkDispatchForErrors = useCallback(
        (index: number) => {
            const isGeneralErrors = checkDispatchForGeneralErrors(index);
            const isHaulToFromErrors = checkDispatchForHaulToFromErrors(index);
            const isBillingErrors = checkDispatchForBillingErrors(index);

            return isGeneralErrors || isHaulToFromErrors || isBillingErrors;
        },
        [checkDispatchForGeneralErrors, checkDispatchForHaulToFromErrors, checkDispatchForBillingErrors]
    );

    const buildDriverString = useCallback((driver: DriverDto | undefined) => {
        if (!!driver) {
            return `${driver.code} - ${driver.name}`;
        } else {
            return '';
        }
    }, []);

    const hasHaulQuantityOnLineItem = useCallback((dispatchReview: DispatchReviewDto) => {
        return dispatchReview.orderLineItem?.orderBy !== OrderByType.Equipment && !(dispatchReview.orderLineItem?.lineItem?.type === LineItemTypes.Hourly && dispatchReview.orderLineItem?.orderBy === OrderByType.Quantity);
    }, []);

    const generalDetails = useCallback(
        (dispatchReview: DispatchReviewDto, index: number) => {
            const orderReviewLineItem = dispatchReview.orderLineItem!;
            const isLineItemCanceled = !!orderReviewLineItem.canceledOn;
            return (
                <Box key={getUniqueKey(dispatchReview)}>
                    {isLineItemCanceled && (
                        <Box mb={1} p={1} mx={2} mt={1} bgcolor='action.disabledBackground' borderRadius={1}>
                            <Typography variant='body2' color='text.secondary'>
                                <strong>Canceled</strong>
                                {orderReviewLineItem.cancellationReason ? ` — Reason: ${orderReviewLineItem.cancellationReason}` : ''}
                                {orderReviewLineItem.canceledByUser ? ` — By: ${orderReviewLineItem.canceledByUser}` : ''}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ pointerEvents: isLineItemCanceled ? 'none' : undefined, opacity: isLineItemCanceled ? 0.5 : 1 }}>
                        <TicketFormGeneralTab orderReview={dispatchReview.orderReview!} orderLineItem={orderReviewLineItem} />
                    </Box>
                </Box>
            );
        }, []);

    const dispatchDetails = useCallback(
        (dispatchReview: DispatchReviewDto, index: number) => {
            var errors = dispatchErrors.get(index);
            const hasHaulQuantity = hasHaulQuantityOnLineItem(dispatchReview);
            const isLineItemCanceled = !!dispatchReview.orderLineItem?.canceledOn;
            const fieldDisabled = disabled || isLineItemCanceled;

            return (
                <Grid item container direction='column' key={getUniqueKey(dispatchReview)}>
                    <Grid item container direction='row' xs={12} spacing={1}>
                        <Grid item xs={12} xl={!hasHaulQuantity ? 4 : 3}>
                            <FormControl fullWidth disabled={fieldDisabled} error={!!errors?.get('assignmentType')}>
                                <FormLabel>Assign. Type</FormLabel>
                                <Select
                                    value={dispatchReview.assignmentType === AssignmentType.Broker ? 'Broker' : 'Driver'}
                                    disabled={true}
                                    onChange={(event: SelectChangeEvent) => {
                                        const value = event.target.value === 'Broker' ? AssignmentType.Broker : AssignmentType.Driver;
                                        updateDispatch(index, 'assignmentType', value);
                                    }}>
                                    <MenuItem value={'Broker'}>Broker</MenuItem>
                                    <MenuItem value={'Driver'}>Driver</MenuItem>
                                </Select>
                                {errors?.get('assignmentType') && <FormHelperText>{errors?.get('assignmentType')}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        {dispatchReview.assignmentType === AssignmentType.Driver && (
                            <Grid item xs={12} xl={!hasHaulQuantity ? 4 : 3}>
                                <FormControl error={!!errors?.get('driverId')} fullWidth required disabled={fieldDisabled}>
                                    <FormLabel>Driver</FormLabel>
                                    <IEntityAutocomplete
                                        options={drivers}
                                        onChange={(e, value) => {
                                            handleDriverChange(index, value ?? null);
                                        }}
                                        value={dispatchReview.driver}
                                        getOptionLabel={(option: DriverDto) => buildDriverString(option)}
                                        error={!!errors?.get('driverId')}
                                        disabled={true}
                                    />
                                    {errors?.get('driverId') && <FormHelperText>{errors?.get('driverId')}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        )}
                        {dispatchReview.assignmentType === AssignmentType.Broker && (
                            <Grid item xs={12} xl={!hasHaulQuantity ? 4 : 3}>
                                <FormControl error={!!errors?.get('brokerId')} fullWidth required disabled={fieldDisabled}>
                                    <FormLabel>Broker</FormLabel>
                                    <IEntityAutocomplete
                                        options={brokers}
                                        onChange={(e, value) => {
                                            handleBrokerChange(index, value ?? null);
                                        }}
                                        value={dispatchReview.broker}
                                        getOptionLabel={(option: BrokerDto) => `${option.code} - ${option.name}`}
                                        error={!!errors?.get('brokerId')}
                                        disabled={true}
                                    />
                                    {errors?.get('brokerId') && <FormHelperText>{errors?.get('brokerId')}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={12} xl={!hasHaulQuantity ? 4 : 3}>
                            <EquipmentSelect
                                selectedEquipmentId={dispatchReview.equipmentId ?? ''}
                                handleSelectedEquipmentChange={(e) => handleEquipmentChange(index, e ?? undefined)}
                                brokerId={selectedIndex !== undefined ? dispatchReviews[selectedIndex]?.brokerId : undefined}
                                disabled={true}
                            />
                        </Grid>
                        {hasHaulQuantity &&
                            <Grid item xs={12} xl={3}>
                                <FormInput
                                    label='Haul Quantity'
                                    fullWidth
                                    value={String(dispatchReview.haulQuantity ?? 0)}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </Grid>
                        }
                        <Grid item xs={12} xl={4}>
                            <TimePicker
                                value={dispatchReview.yardTime}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(dispatchReview.orderLineItem?.orderReview?.orderDate ?? null, date);
                                        updateDispatch(index, 'yardTime', adjustedDate);
                                    }
                                }}
                                label='Yard Time'
                                disabled={fieldDisabled}
                                error={!!errors?.get('yardTime')}
                                errorText={errors?.get('yardTime')}
                            />
                        </Grid>
                        <Grid item xs={12} xl={4}>
                            <TimePicker
                                value={dispatchReview.startDate}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(dispatchReview.orderLineItem?.orderReview?.orderDate ?? null, date);
                                        updateDispatch(index, 'startDate', adjustedDate);
                                    }
                                }}
                                error={!!errors?.get('startDate')}
                                errorText={errors?.get('startDate')}
                                label='Onsite Time'
                                disabled={fieldDisabled}
                            />
                        </Grid>
                        <Grid item xs={12} xl={4}>
                            <TimePicker
                                value={dispatchReview.endDate}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(dispatchReview.orderLineItem?.orderReview?.orderDate ?? null, date);
                                        updateDispatch(index, 'endDate', adjustedDate);
                                    }
                                }}
                                error={!!errors?.get('endDate')}
                                errorText={errors?.get('endDate')}
                                label='End Time'
                                disabled={fieldDisabled}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <FormInput
                            label='Driver Comment'
                            fullWidth
                            value={dispatchReview.driverComment}
                            onChange={(e) => updateDispatch(index, 'driverComment', e.target.value)}
                            rows={2}
                            disabled={fieldDisabled}
                        />
                    </Grid>
                </Grid>
            );
        },
        [
            brokers,
            buildDriverString,
            disabled,
            drivers,
            handleBrokerChange,
            handleDriverChange,
            handleEquipmentChange,
            updateDispatch,
            dispatchErrors,
            dispatchReviews,
            selectedIndex,
            hasHaulQuantityOnLineItem
        ]
    );

    const haulToFromDetails = useCallback(
        (dispatchReview: DispatchReviewDto, index: number) => {
            const orderReviewLineItem = dispatchReview.orderLineItem;
            const isLineItemCanceled = !!orderReviewLineItem?.canceledOn;
            return (
                <Box p={2} sx={{ pointerEvents: isLineItemCanceled ? 'none' : undefined, opacity: isLineItemCanceled ? 0.5 : 1 }}>
                    <HaulToFromForm
                        haulToAddressLine1={orderReviewLineItem?.haulToAddressLine1 ?? ''}
                        haulToAddressLine2={orderReviewLineItem?.haulToAddressLine2 ?? ''}
                        haulToCity={orderReviewLineItem?.haulToCity ?? ''}
                        haulToState={orderReviewLineItem?.haulToState ?? ''}
                        haulToZipCode={orderReviewLineItem?.haulToZipCode ?? ''}
                        haulToCountry={orderReviewLineItem?.haulToCountry ?? ''}
                        haulToDirections={orderReviewLineItem?.haulToDirections ?? ''}
                        haulFromAddressLine1={orderReviewLineItem?.haulFromAddressLine1 ?? ''}
                        haulFromAddressLine2={orderReviewLineItem?.haulFromAddressLine2 ?? ''}
                        haulFromCity={orderReviewLineItem?.haulFromCity ?? ''}
                        haulFromState={orderReviewLineItem?.haulFromState ?? ''}
                        haulFromZipCode={orderReviewLineItem?.haulFromZipCode ?? ''}
                        haulFromCountry={orderReviewLineItem?.haulFromCountry ?? ''}
                        haulFromDirections={orderReviewLineItem?.haulFromDirections ?? ''}
                        enableLookup={false}
                        regions={regions?.pageResults ?? []}
                        haulFromRegion={orderReviewLineItem?.haulFromRegion}
                        haulToRegion={orderReviewLineItem?.haulToRegion}
                        haulToSiteName={orderReviewLineItem?.haulToSiteName}
                        haulFromSiteName={orderReviewLineItem?.haulFromSiteName}
                        disabled
                    />
                </Box>
            );
        },
        [regions]
    );

    const billingDetails = useCallback(
        (dispatchReview: DispatchReviewDto, dispatchReviewIndex: number) => {
            const orderId = dispatchReviews.find(dispatch => dispatch.id === dispatchReview.id)?.orderId;
            const order = getOrderById(orderId ?? '');
            const orderReviewLineItem = dispatchReviews.find(dispatch => dispatch.id === dispatchReview.id)?.orderLineItem;
            const isLineItemCanceled = !!orderReviewLineItem?.canceledOn;
            const fieldDisabled = disabled || isLineItemCanceled;

            return (
                <Grid item container direction='column' height='100%' wrap='nowrap'>
                    {(dispatchReview.freightBillingLineItems ?? []).length === 0 && (
                        <Grid item textAlign='center' xs={12}>
                            <Typography color='GrayText'>No billing items have been added to this dispatch.</Typography>
                        </Grid>
                    )}
                    {(dispatchReview.freightBillingLineItems ?? []).length > 0 && order && orderReviewLineItem && (
                        <Grid item xs={12}>
                            <FreightBillingLineItemTable
                                disabled={fieldDisabled}
                                orderReviewLineItem={orderReviewLineItem}
                                order={order}
                                dispatchReview={dispatchReview}
                                removeFreightBillingLineItem={handleRemoveFreightBillingLineItem(dispatchReviewIndex)}
                                updateFreightBillingLineItemField={(
                                    lineItemIndex,
                                    key,
                                    value
                                ) => {
                                    if (key === 'freightBillNumber') {
                                        handleUpdatedFreightBillingNumber(value as string, dispatchReviewIndex, lineItemIndex);
                                    }
                                    setDispatches((prev: DispatchReviewDto[]) => {
                                        return prev.map((dispatch, i) => {
                                            if (i === dispatchReviewIndex) {
                                                return {
                                                    ...dispatch,
                                                    freightBillingLineItems: dispatch.freightBillingLineItems?.map((lineItem, j) => {
                                                        if (j === lineItemIndex) {
                                                            return {
                                                                ...lineItem,
                                                                [key]: value
                                                            };
                                                        }
                                                        return lineItem;
                                                    })
                                                };
                                            }
                                            return dispatch;
                                        });
                                    });
                                }}
                                updateFreightBillingLineItem={(freightLineItemIndex, freightLineItem) => {
                                    setDispatches((prev: DispatchReviewDto[]) => {
                                        return prev.map((dispatch, i) => {
                                            if (i === dispatchReviewIndex) {
                                                return {
                                                    ...dispatch,
                                                    freightBillingLineItems: dispatch.freightBillingLineItems?.map((lineItem, j) => {
                                                        if (j === freightLineItemIndex) {
                                                            return freightLineItem;
                                                        }
                                                        return lineItem;
                                                    })
                                                };
                                            }
                                            return dispatch;
                                        });
                                    });
                                }}
                                freightErrors={freightErrors.get(dispatchReviewIndex) ?? new Map<number, Map<keyof FreightBillingLineItemDto, string>>()}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Tooltip title='Add Freight Billing'>
                            <IconButton
                                disabled={fieldDisabled}
                                size='small'
                                color='primary'
                                sx={{ marginTop: '12px' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    orderReviewLineItem && addFreightBillingLineItem(dispatchReviewIndex);
                                }}>
                                <AddCircle />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            );
        },
        [getOrderById, disabled, freightErrors, dispatchReviews, setDispatches, addFreightBillingLineItem, handleRemoveFreightBillingLineItem, handleUpdatedFreightBillingNumber]
    );

    const memosTab = useCallback((dispatchReview: DispatchReviewDto, dispatchReviewIndex: number) => {
        const orderId = dispatchReviews.find(dispatch => dispatch.id === dispatchReview.id)?.orderId;
        const order = getOrderById(orderId ?? '');
        const orderReviewLineItem = dispatchReviews.find(dispatch => dispatch.id === dispatchReview.id)?.orderLineItem;
        const isLineItemCanceled = !!orderReviewLineItem?.canceledOn;
        const fieldDisabled = disabled || isLineItemCanceled;
        return (
            <Grid item container direction='column' height='100%' wrap='nowrap' spacing={1}>
                <Grid item xs={12}>
                    {isLineItemCanceled && (
                        <Box mb={1} p={1} bgcolor='action.disabledBackground' borderRadius={1}>
                            <Typography variant='body2' color='text.secondary'>
                                <strong>Canceled</strong>
                                {orderReviewLineItem?.cancellationReason ? ` — Reason: ${orderReviewLineItem.cancellationReason}` : ''}
                                {orderReviewLineItem?.canceledByUser ? ` — By: ${orderReviewLineItem.canceledByUser}` : ''}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ pointerEvents: isLineItemCanceled ? 'none' : undefined, opacity: isLineItemCanceled ? 0.5 : 1 }}>
                    <MemoForm
                        quoteMemo={order?.quote?.memo ?? ''}
                        lineItemMemo={orderReviewLineItem?.lineItem?.lineItemMemo ?? ''}
                        dispatchBillingMemo={orderReviewLineItem?.dispatchBillingMemo ?? ''}
                        orderLineItemDriverMemo={orderReviewLineItem?.driverMemo}
                        hideDriverComment={true}
                        disabled={fieldDisabled}
                        handleOrderLineItemDriverMemoChange={(value) => {
                            updateOrderReviewLineItemMemos(dispatchReviewIndex, 'driverMemo', value);
                        }}
                        handleDispatchBillingMemoChange={(value) => {
                            updateOrderReviewLineItemMemos(dispatchReviewIndex, 'dispatchBillingMemo', value);
                        }}
                    />
                    </Box>
                </Grid>
            </Grid>
        );
    }, [disabled, dispatchReviews, getOrderById, updateOrderReviewLineItemMemos]);

    const details = useCallback(
        (index?: number) => {
            const item = dispatchReviews.at(index ?? 0);
            const isDispatchErrors = index !== undefined ? checkDispatchForGeneralErrors(index) : false;
            const isGeneralErrors = false;
            const isHaulToFromErrors = index !== undefined ? checkDispatchForHaulToFromErrors(index) : false;
            const isBillingErrors = index !== undefined ? checkDispatchForBillingErrors(index) : false;
            return (
                <Box key={item?.id} sx={{ width: '100%', borderBottom: 1, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                    <TabContext value={item ? selectedTab : '0'}>
                        <Box sx={{ width: '100%', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
                            <TabList variant='fullWidth' sx={{ width: '100%' }} onChange={(_e, newValue) => setSelectedTab(newValue)}>
                                <Tab
                                    label='General'
                                    value='0'
                                    iconPosition='end'
                                    icon={(isGeneralErrors || isHaulToFromErrors) ? <Error color='error' /> : undefined}
                                    sx={{ minHeight: '48px' }}
                                />
                                <Tab
                                    label='Dispatch/Memos'
                                    value='1'
                                    iconPosition='end'
                                    icon={isDispatchErrors ? <Error color='error' /> : undefined}
                                    sx={{ minHeight: '48px' }}
                                />
                                <Tab
                                    label='Billing'
                                    value='2'
                                    iconPosition='end'
                                    icon={isBillingErrors ? <Error color='error' /> : undefined}
                                    sx={{ minHeight: '48px' }}
                                />
                            </TabList>
                        </Box>
                        {!item && (
                            <Box textAlign='center'>
                                <Typography mt={2} color='GrayText'>
                                    No Item Selected
                                </Typography>
                            </Box>
                        )}
                        <TabPanel value='0' sx={{ padding: '5px', overflowY: 'auto' }}>
                            {item && index !== undefined && (
                                <>
                                    {generalDetails(item, index)}
                                    <Box mt={2} />
                                    {haulToFromDetails(item, index)}
                                </>
                            )}
                        </TabPanel>
                        <TabPanel value='1' sx={{ overflowY: 'auto' }}>
                            {item && index !== undefined && (
                                <>
                                    {dispatchDetails(item, index)}
                                    <Box mt={2} />
                                    {memosTab(item, index)}
                                </>
                            )}
                        </TabPanel>
                        <TabPanel value='2' sx={{ overflowY: 'auto' }}>
                            {item && index !== undefined && billingDetails(item, index)}
                        </TabPanel>
                    </TabContext>
                </Box>
            );
        },
        [dispatchReviews, checkDispatchForGeneralErrors, checkDispatchForHaulToFromErrors, checkDispatchForBillingErrors, selectedTab, generalDetails, memosTab, dispatchDetails, haulToFromDetails, billingDetails]
    );

    const getDesc = useCallback((item: DispatchReviewDto) => {
        const customerNumber = item.orderLineItem?.orderReview?.customerNumber ? `${item.orderLineItem.orderReview.customerNumber} - ` : '';
        return `${customerNumber}${item.orderLineItem?.customerName} - ${item.orderLineItem?.description ?? ''} - ${item.orderLineItem?.poNumber ?? item.orderLineItem?.orderReview?.poNumber ?? item.orderLineItem?.lineItem?.poNumber ?? ''}`;
    }, []);

    const ticketEntryForm = useMemo(() => {
        return <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', flexGrow: 1, height: '100%' }}>
            <SelectableList
                items={dispatchReviews}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                hasError={checkDispatchForErrors}
                getKey={(item, index) => (item as DispatchReviewDto & { clientTempId?: string }).clientTempId ?? item.id ?? `dispatch-${index}`}
                getDesc={getDesc}
                style={{
                    borderRight: 1,
                    borderLeft: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                    minWidth: 240,
                    height: '100%',
                    bgcolor: 'background.paper',
                    overflowY: 'auto'
                }}
                disableAdd={disableAdd}
                disableRemove={() => true}
                addItem={() => {
                    setSelectOrderReviewLineItemDialogOpen(true);
                }}
            />
            {details(selectedIndex)}
        </Box>;
    }, [checkDispatchForErrors, details, dispatchReviews, getDesc, selectedIndex, disableAdd]);

    if (!dispatchReviews || selectedIndex === undefined) {
        return <></>;
    }

    return (
        <>
            {ticketEntryForm}
            <SelectOrderReviewLineItemStepperDialog
                open={selectOrderReviewLineItemDialogOpen}
                onClose={() => setSelectOrderReviewLineItemDialogOpen(false)}
                onComplete={(orderReview, orderReviewLineItem) => {
                    addDispatchReviewForOrderReviewLineItem(orderReview, orderReviewLineItem);
                }}
                date={date}
            />
        </>
    );
};
